// TurmasView — CRUD de turmas acadêmicas (academic, não confundir com groups)

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Archive,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Users,
  UsersRound,
  X
} from "lucide-react";
import { Switch } from "@app/ui/components/switch";
import { api, ApiError, type TurmaDetail, type TurmaRow } from "../api";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const turmas = signal<TurmaRow[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const editing = signal<TurmaDetail | null>(null);
const creating = signal<boolean>(false);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listTurmas();
    turmas.value = r.turmas;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

async function openEdit(id: string) {
  try {
    const r = await api.getTurma(id);
    editing.value = r.turma;
  } catch (e) {
    void openAlert({ title: "Erro", message: friendlyError(e) });
  }
}

export function TurmasView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {errMsg.value}
      </div>
    );
  const rows = turmas.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = useMemo(() => {
    return rows.filter((t) => {
      if (q) {
        const hay = `${t.name} ${t.code ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, q]);

  return (
    <div class="space-y-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="text-2xl font-bold tracking-tight">Turmas</h1>
          <p class="mt-1 text-sm text-muted-foreground">Turmas acadêmicas com matrículas.</p>
        </div>
        <button
          type="button"
          onClick={() => (creating.value = true)}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" /> Nova turma
        </button>
      </header>
      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar nome, código…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>
      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma turma encontrada.
        </div>
      ) : (
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((t) => (
            <div key={t.id} class="space-y-2 rounded-md border border-border bg-card/40 p-4">
              <div class="flex items-start justify-between gap-2">
                <div class="min-w-0 flex-1">
                  <h3 class="truncate text-sm font-bold">{t.name}</h3>
                  {t.code && <p class="font-mono text-[10px] text-muted-foreground">{t.code}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(t.id)}
                  class="rounded-md border border-border bg-background p-1.5 text-xs hover:bg-accent"
                  title="Editar"
                  aria-label="Editar"
                >
                  <Pencil width="0.9em" height="0.9em" />
                </button>
              </div>
              <div class="flex items-center gap-2 text-[11px]">
                <span class="inline-flex items-center gap-1 rounded bg-accent/60 px-1.5 py-0.5 font-bold tabular-nums">
                  <Users width="0.9em" height="0.9em" />
                  {t.enrolledCount} alunos
                </span>
                {t.archivedAt && (
                  <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700 dark:text-amber-300">
                    <Archive width="0.85em" height="0.85em" /> Arquivada
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {editing.value && <EditModal turma={editing.value} />}
      {creating.value && <CreateModal />}
    </div>
  );
}

function CreateModal() {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.createTurma({ name: name.trim(), code: code.trim() || null });
      creating.value = false;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <ModalShell title="Nova turma" onClose={() => (creating.value = false)}>
      <Field label="Nome *">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          autoFocus
          aria-label="Nome"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Código">
        <input
          type="text"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          aria-label="Código"
          placeholder="Ex: T01"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm outline-none focus:border-primary"
        />
      </Field>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (creating.value = false)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={busy || !name.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Plus width="1em" height="1em" />}
          Criar
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function EditModal({ turma }: { turma: TurmaDetail }) {
  const [detail, setDetail] = useState<TurmaDetail>(turma);
  const [name, setName] = useState(turma.name);
  const [code, setCode] = useState(turma.code ?? "");
  const [archived, setArchived] = useState(!!turma.archivedAt);
  const [busy, setBusy] = useState(false);
  const [userInput, setUserInput] = useState("");

  async function save() {
    setBusy(true);
    try {
      const r = await api.patchTurma(turma.id, {
        name: name.trim(),
        code: code.trim() || null,
        archived
      });
      setDetail(r.turma);
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  async function addStudent() {
    if (!userInput.trim()) return;
    try {
      const r = await api.enrollStudent(turma.id, userInput.trim());
      setDetail(r.turma);
      setUserInput("");
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }
  async function removeStudent(uid: string) {
    if (!confirm("Desmatricular este aluno?")) return;
    try {
      const r = await api.unenrollStudent(turma.id, uid);
      setDetail(r.turma);
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    }
  }
  return (
    <ModalShell title={`Editar: ${detail.name}`} onClose={() => (editing.value = null)} wide>
      <Field label="Nome">
        <input
          type="text"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          aria-label="Nome"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Código">
        <input
          type="text"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          aria-label="Código"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm outline-none focus:border-primary"
        />
      </Field>
      <label class="flex items-center gap-2 text-xs">
        <Switch checked={archived} onCheckedChange={(v) => setArchived(v)} />
        Arquivar turma
      </label>
      <div class="rounded-md border border-border bg-background/50 p-3">
        <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <Users width="1em" height="1em" /> Alunos matriculados ({detail.enrollments.filter((e) => !e.withdrawnAt).length})
        </h3>
        <ul class="mb-2 max-h-[200px] space-y-1 overflow-y-auto">
          {detail.enrollments
            .filter((e) => !e.withdrawnAt)
            .map((e) => (
              <li
                key={e.userId}
                class="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 text-xs"
              >
                <span class="min-w-0 flex-1 truncate font-semibold">{e.name}</span>
                <span class="truncate font-mono text-[10px] text-muted-foreground">{e.email}</span>
                <button
                  type="button"
                  onClick={() => removeStudent(e.userId)}
                  class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  title="Desmatricular"
                  aria-label="Desmatricular"
                >
                  <X width="0.9em" height="0.9em" />
                </button>
              </li>
            ))}
        </ul>
        <div class="flex gap-1.5">
          <input
            type="email"
            value={userInput}
            onInput={(e) => setUserInput((e.target as HTMLInputElement).value)}
            placeholder="Email do aluno pra matricular"
            aria-label="Email do aluno"
            class="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
          />
          <button
            type="button"
            onClick={addStudent}
            disabled={!userInput.trim()}
            class="rounded-md border border-border bg-background px-2 py-1 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            + Matricular
          </button>
        </div>
      </div>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (editing.value = null)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Fechar
        </button>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Save width="1em" height="1em" />}
          Salvar
        </button>
      </ModalFooter>
    </ModalShell>
  );
}

function ModalShell({
  title,
  onClose,
  children,
  wide
}: {
  title: string;
  onClose: () => void;
  children: any;
  wide?: boolean;
}) {
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class={`flex max-h-[92vh] w-full ${wide ? "max-w-2xl" : "max-w-lg"} flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <UsersRound width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
function ModalFooter({ children }: { children: any }) {
  return (
    <div class="sticky bottom-0 -mx-4 -mb-4 mt-2 flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
      {children}
    </div>
  );
}
